import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";

const empty = { min_density: 0, max_density: 0, freight_class: "" };

export default function FreightClasses() {
  const [bands, setBands] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/freight-class").then((r) => setBands(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { min_density: Number(form.min_density), max_density: Number(form.max_density), freight_class: form.freight_class };
    try {
      if (editing) await api.put(`/freight-class/${editing.id}`, payload);
      else await api.post("/freight-class", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete class ${b.freight_class} band?`)) return;
    try { await api.delete(`/freight-class/${b.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">Freight Class Lookup</h1>
          <p className="mt-1 text-sm text-muted-foreground">Density bands → NMFC class · overlaps are blocked</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-band-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Plus className="mr-1.5 h-4 w-4" />Add Band</Button>
      </div>

      <div className="max-w-2xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-right">Min Density</TableHead>
              <TableHead className="text-right">Max Density</TableHead>
              <TableHead>Freight Class</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bands.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground"><Layers className="mx-auto mb-2 h-7 w-7 opacity-40" />No bands.</TableCell></TableRow>}
            {bands.map((b) => (
              <TableRow key={b.id} className="border-border hover:bg-secondary/40" data-testid={`band-row-${b.id}`}>
                <TableCell className="metric-num text-right">{b.min_density}</TableCell>
                <TableCell className="metric-num text-right">{b.max_density}</TableCell>
                <TableCell><span className="font-semibold text-[hsl(var(--primary))]">Class {b.freight_class}</span></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(b); setForm(b); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" onClick={() => del(b)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="band-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Band" : "Add Band"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="label-caps">Min Density</Label><Input type="number" value={form.min_density} onChange={(e) => setForm({ ...form, min_density: e.target.value })} className="mt-1.5 h-10" data-testid="band-min" /></div>
            <div><Label className="label-caps">Max Density</Label><Input type="number" value={form.max_density} onChange={(e) => setForm({ ...form, max_density: e.target.value })} className="mt-1.5 h-10" data-testid="band-max" /></div>
            <div><Label className="label-caps">Class</Label><Input value={form.freight_class} onChange={(e) => setForm({ ...form, freight_class: e.target.value })} className="mt-1.5 h-10" data-testid="band-class" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-band-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
